import React, { useCallback, useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, ChevronLeft, ChevronRight } from 'lucide-react';
import { openWhatsApp, getCabinMessage } from '../utils/whatsapp';
interface CabinGalleryModalProps {
  cabinName: string;
  images: string[];
  startIndex?: number;
  onClose: () => void;
}
export function CabinGalleryModal({
  cabinName,
  images,
  startIndex = 0,
  onClose
}: CabinGalleryModalProps) {
  const [current, setCurrent] = useState(startIndex);
  const goPrev = useCallback(() => {
    setCurrent(c => (c - 1 + images.length) % images.length);
  }, [images.length]);
  const goNext = useCallback(() => {
    setCurrent(c => (c + 1) % images.length);
  }, [images.length]);
  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
      if (e.key === 'ArrowLeft') goPrev();
      if (e.key === 'ArrowRight') goNext();
    };
    document.addEventListener('keydown', handleKey);
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', handleKey);
      document.body.style.overflow = '';
    };
  }, [goPrev, goNext, onClose]);
  return <AnimatePresence data-id="element-30">
      <motion.div initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} exit={{
      opacity: 0
    }} className="fixed inset-0 z-[100] flex flex-col bg-charcoal/95 backdrop-blur-sm" role="dialog" aria-modal="true" aria-label={`Galería de la cabaña ${cabinName}`} onClick={onClose} data-id="element-31">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-5 text-white" onClick={e => e.stopPropagation()} data-id="element-32">
          <div data-id="element-33">
            <h3 className="font-serif text-2xl" data-id="element-34">{cabinName}</h3>
            <p className="text-sm text-white/60" data-id="element-35">
              {current + 1} / {images.length}
            </p>
          </div>
          <button onClick={onClose} aria-label="Cerrar galería" className="p-2 rounded-full hover:bg-white/10 transition-colors" data-id="element-36">
            <X size={28} data-id="element-37" />
          </button>
        </div>

        {/* Main image */}
        <div className="flex-1 flex items-center justify-center px-4 sm:px-16 relative min-h-0" onClick={e => e.stopPropagation()} data-id="element-38">
          {images.length > 1 && <button onClick={goPrev} aria-label="Foto anterior" className="absolute left-2 sm:left-6 z-10 p-2 sm:p-3 rounded-full bg-white/10 hover:bg-white/20 text-white transition-colors" data-id="element-39">
              <ChevronLeft size={28} data-id="element-40" />
            </button>}

          <motion.img key={current} initial={{
          opacity: 0,
          scale: 0.98
        }} animate={{
          opacity: 1,
          scale: 1
        }} transition={{
          duration: 0.25
        }} src={images[current]} alt={`${cabinName} — foto ${current + 1}`} className="max-h-full max-w-full object-contain rounded-lg shadow-2xl" data-id="element-41" />

          {images.length > 1 && <button onClick={goNext} aria-label="Foto siguiente" className="absolute right-2 sm:right-6 z-10 p-2 sm:p-3 rounded-full bg-white/10 hover:bg-white/20 text-white transition-colors" data-id="element-42">
              <ChevronRight size={28} data-id="element-43" />
            </button>}
        </div>

        {/* Thumbnails + CTA */}
        <div className="px-6 py-5" onClick={e => e.stopPropagation()} data-id="element-44">
          {images.length > 1 && <div className="flex gap-2 overflow-x-auto justify-center mb-5 pb-1" data-id="element-45">
              {images.map((img, i) => <button key={i} onClick={() => setCurrent(i)} aria-label={`Ver foto ${i + 1}`} className={`flex-shrink-0 w-16 h-16 rounded-lg overflow-hidden border-2 transition-all ${i === current ? 'border-white opacity-100' : 'border-transparent opacity-50 hover:opacity-80'}`} data-id="element-46">
                  <img src={img} alt="" className="w-full h-full object-cover" data-id="element-47" />
                </button>)}
            </div>}
          <div className="flex justify-center" data-id="element-48">
            <button onClick={() => openWhatsApp(getCabinMessage(cabinName))} className="px-8 py-3 bg-olive text-white rounded-full font-medium hover:bg-olive-dark transition-colors shadow-lg" data-id="element-49">
              Consultar disponibilidad
            </button>
          </div>
        </div>
      </motion.div>
    </AnimatePresence>;
}